/**
 * Usage Tracking Service
 * Tracks monthly minutes and recording limits per user
 */

import { webStorageService } from './webStorageService';

// Usage limits
const MONTHLY_MINUTES_LIMIT = 600; // 10 hours per month
const MAX_RECORDING_MINUTES = 5; // 5 minutes per recording

class UsageTrackingService {
  constructor() {
    this.currentUserId = null;
  }

  /**
   * Set current user for usage tracking
   * @param {string} userId
   */
  setCurrentUser(userId) {
    console.log('UsageTracking: Setting current user:', userId);
    this.currentUserId = userId;
    webStorageService.setCurrentUser(userId);
  }

  /**
   * Get current usage data for the user
   * @returns {Promise<Object>}
   */
  async getCurrentUsage() {
    try {
      const usage = await webStorageService.getItem('usage_tracking') || {
        monthly_minutes_used: 0,
        last_reset_date: new Date().toISOString().substring(0, 7), // YYYY-MM format
        current_month_recordings: 0
      };

      // Check if we need to reset for new month
      const currentMonth = new Date().toISOString().substring(0, 7);
      if (usage.last_reset_date !== currentMonth) {
        usage.monthly_minutes_used = 0;
        usage.current_month_recordings = 0;
        usage.last_reset_date = currentMonth;
        await this.saveUsage(usage);
      }

      return usage;
    } catch (error) {
      console.error('Error getting current usage:', error);
      return {
        monthly_minutes_used: 0,
        last_reset_date: new Date().toISOString().substring(0, 7),
        current_month_recordings: 0
      };
    }
  }

  /**
   * Save usage data
   * @param {Object} usage
   * @returns {Promise<boolean>}
   */
  async saveUsage(usage) {
    try {
      return await webStorageService.setItem('usage_tracking', usage);
    } catch (error) {
      console.error('Error saving usage:', error);
      return false;
    }
  }

  /**
   * Check if user can start a new recording
   * @returns {Promise<Object>} {canRecord: boolean, reason?: string, minutesRemaining: number}
   */
  async canStartRecording() {
    try {
      const usage = await this.getCurrentUsage();
      const minutesRemaining = MONTHLY_MINUTES_LIMIT - usage.monthly_minutes_used;

      if (usage.monthly_minutes_used >= MONTHLY_MINUTES_LIMIT) {
        return {
          canRecord: false,
          reason: 'Monthly limit reached',
          minutesRemaining: 0
        };
      }

      return {
        canRecord: true,
        minutesRemaining: minutesRemaining
      };
    } catch (error) {
      console.error('Error checking recording permission:', error);
      return {
        canRecord: false,
        reason: 'Error checking usage limits',
        minutesRemaining: 0
      };
    }
  }

  /**
   * Add minutes to the user's monthly usage
   * @param {number} minutes
   * @returns {Promise<boolean>}
   */
  async addMinutesUsed(minutes) {
    try {
      console.log('UsageTracking: Adding minutes used:', minutes);
      const usage = await this.getCurrentUsage();
      console.log('UsageTracking: Current usage before update:', usage);

      usage.monthly_minutes_used += minutes;
      usage.current_month_recordings += 1;

      console.log('UsageTracking: Usage after update:', usage);
      const result = await this.saveUsage(usage);
      console.log('UsageTracking: Save result:', result);

      return result;
    } catch (error) {
      console.error('Error adding minutes used:', error);
      return false;
    }
  }

  /**
   * Get usage summary for display
   * @returns {Promise<Object>}
   */
  async getUsageSummary() {
    try {
      const usage = await this.getCurrentUsage();
      const minutesRemaining = Math.max(0, MONTHLY_MINUTES_LIMIT - usage.monthly_minutes_used);

      return {
        minutesUsed: usage.monthly_minutes_used,
        minutesRemaining: minutesRemaining,
        monthlyLimit: MONTHLY_MINUTES_LIMIT,
        maxRecordingMinutes: MAX_RECORDING_MINUTES,
        recordingsThisMonth: usage.current_month_recordings,
        lastResetDate: usage.last_reset_date
      };
    } catch (error) {
      console.error('Error getting usage summary:', error);
      return {
        minutesUsed: 0,
        minutesRemaining: MONTHLY_MINUTES_LIMIT,
        monthlyLimit: MONTHLY_MINUTES_LIMIT,
        maxRecordingMinutes: MAX_RECORDING_MINUTES,
        recordingsThisMonth: 0,
        lastResetDate: new Date().toISOString().substring(0, 7)
      };
    }
  }

  /**
   * Get constants for UI
   * @returns {Object}
   */
  getLimits() {
    return {
      MONTHLY_MINUTES_LIMIT,
      MAX_RECORDING_MINUTES
    };
  }

  /**
   * Format minutes for display (e.g., "125 minutes (2h 5m)")
   * @param {number} minutes
   * @returns {string}
   */
  formatMinutes(minutes) {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${minutes} minutes (${hours}h)`;
    }

    return `${minutes} minutes (${hours}h ${remainingMinutes}m)`;
  }

  /**
   * Check if recording should be stopped due to time limit
   * @param {number} currentRecordingSeconds
   * @returns {boolean}
   */
  shouldStopRecording(currentRecordingSeconds) {
    const currentRecordingMinutes = Math.floor(currentRecordingSeconds / 60);
    return currentRecordingMinutes >= MAX_RECORDING_MINUTES;
  }

  /**
   * Get time remaining in current recording (in seconds)
   * @param {number} currentRecordingSeconds
   * @returns {number}
   */
  getRemainingRecordingTime(currentRecordingSeconds) {
    const maxSeconds = MAX_RECORDING_MINUTES * 60;
    return Math.max(0, maxSeconds - currentRecordingSeconds);
  }
}

// Create and export singleton instance
export const usageTrackingService = new UsageTrackingService();
export default usageTrackingService;